import { useEffect, useRef } from 'react';

/**
 * Polls the BE /status endpoint and pre-empts the FE step pointer when
 * LinkedIn challenges 2FA. Three trigger points share this hook:
 *
 *   1. Initial-login 2FA              → step 7 (Step07TwoFA)
 *   2. Badge-flow re-login 2FA        → step 7 (same component, different
 *                                                resume target)
 *   3. Email-change 2FA               → step 17 (Step17EmailChange2FA)
 *
 * The hook does NOT own the step state — it calls the supplied
 * `setStep` and `setSavedStep` so the App-level state machine can
 * record the saved step and restore it on success. The consuming step
 * (Step07/Step17) is responsible for clearing the relevant
 * `awaiting_*_2fa` flag on the BE before the restore path fires.
 *
 * Polling cadence: 3s while a session is active. Cheap — `/status`
 * already returns a small JSON. We pause the poller when no session
 * exists yet (sessionId === null), and when the FE is already on the
 * injection target (no point re-firing).
 *
 * Implementation note: we read mutable values (`currentStep`,
 * `injectionMap`, callbacks) via refs so the effect only re-runs when
 * the session-identity values change — putting `currentStep` in the
 * dep array would tear down and re-create the timer on every step
 * change, which is racy and wasteful.
 */
export default function useInjection({
  sessionId,
  currentStep,
  currentView,
  setStep,
  setSavedStep,
  apiBaseUrl,
  sessionToken,
  injectionMap,
  enabled = true,
  // B-AWAIT-APPROVAL-1 (2026-05-17): optional callback fired on every
  // /status poll with the current `awaiting_app_approval` boolean.
  // App.jsx uses this to drive a GLOBAL overlay that renders the
  // polished ApproveOnPhone card on top of WHATEVER step the seller
  // is on. Solves the late-mount bug where a per-step SSE listener
  // misses the needs_app_approval event because the relevant
  // component hasn't mounted yet. No-op if the consumer doesn't pass
  // a callback — preserves backward compatibility with existing
  // useInjection callers (legacy injection-map flow unchanged).
  onAwaitingAppApprovalChange,
  // B-FE-FAILED-GATE (2026-05-17): optional callback fired on every
  // transition of `status.status` (the session-level state). App.jsx
  // uses this to detect the BE flipping the session to `'failed'`
  // and surface a terminal error screen instead of letting the
  // wizard walk past. Without the gate, sellers can advance through
  // Step02 → Step20 filling out forms for a session the BE already
  // terminated — burning seller time + producing zombie inputs.
  // Fires with `{ status, failureReason }`. No-op if not passed.
  onSessionStatusChange,
  // B-CREDS-FE-EXPLICIT R2/R3 (2026-05-29): optional callback fired
  // on transitions of the BE's structured wrong-credentials signal.
  // Fires with `{ active, kind, message }`:
  //   active=true when status='waiting_for_credentials_retry' OR
  //     (status='terminal_failed' AND terminalReason=
  //     'credential_retry_limit_exceeded')
  //   kind = 'recoverable' or 'limit_exceeded' so App.jsx can pick the
  //     overlay copy
  //   message = BE-authored statusMessage (preferred) or null
  // active=false fires when the seller's resubmit on /credentials
  // resets the session back to in_progress.
  // No-op if not passed.
  onWrongCredentialsChange,
}) {
  const lastSeenFlagsRef = useRef({});
  const lastApprovalFlagRef = useRef(null);
  const lastSessionStatusRef = useRef(null);
  const lastWrongCredsActiveRef = useRef(null);
  const refs = useRef({
    currentStep, currentView, setStep, setSavedStep, injectionMap,
    onAwaitingAppApprovalChange, onSessionStatusChange,
    onWrongCredentialsChange,
  });
  refs.current = {
    currentStep, currentView, setStep, setSavedStep, injectionMap,
    onAwaitingAppApprovalChange, onSessionStatusChange,
    onWrongCredentialsChange,
  };

  useEffect(() => {
    if (!enabled || !sessionId || !sessionToken || !apiBaseUrl) return undefined;

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      let shouldSkipViewPing = false;
      try {
        const res = await fetch(`${apiBaseUrl}/api/takeover/sessions/${sessionId}/status`, {
          headers: { 'X-Session-Token': sessionToken },
        });
        if (cancelled || !res.ok) return;
        const status = await res.json();

        const r = refs.current;

        // B-AWAIT-APPROVAL-1: surface awaiting_app_approval to App.jsx
        // BEFORE running the legacy injection-map loop. The two are
        // independent — app_approval is a non-input gate (no code to
        // type), so it never pre-empts the step; it just paints the
        // overlay. Only fire the callback on transitions to avoid
        // hammering App.jsx with redundant setState calls every 3s.
        if (typeof r.onAwaitingAppApprovalChange === 'function') {
          const cur = !!status.awaiting_app_approval;
          if (cur !== lastApprovalFlagRef.current) {
            lastApprovalFlagRef.current = cur;
            try { r.onAwaitingAppApprovalChange(cur); } catch (_) { /* noop */ }
          }
        }

        // B-FE-FAILED-GATE (2026-05-17): surface session.status transitions
        // to App.jsx so it can gate forward-nav + render a terminal error
        // screen when the BE flips the session to 'failed'. Only fires on
        // transitions; App.jsx receives both the new status and the
        // failureReason so it can show actionable copy.
        if (typeof r.onSessionStatusChange === 'function') {
          const curStatus = status.status || null;
          if (curStatus !== lastSessionStatusRef.current) {
            lastSessionStatusRef.current = curStatus;
            try {
              r.onSessionStatusChange({
                status: curStatus,
                failureReason: status.failureReason || null,
              });
            } catch (_) { /* noop */ }
          }
        }

        // B-CREDS-FE-EXPLICIT R2/R3 (2026-05-29): surface the structured
        // wrong-credentials signal. Fires on transitions only.
        if (typeof r.onWrongCredentialsChange === 'function') {
          let active = false;
          let kind = null;
          if (status.status === 'waiting_for_credentials_retry') {
            active = true;
            kind = 'recoverable';
          } else if (
            status.status === 'terminal_failed'
            && status.terminalReason === 'credential_retry_limit_exceeded'
          ) {
            active = true;
            kind = 'limit_exceeded';
          }
          if (active !== lastWrongCredsActiveRef.current) {
            lastWrongCredsActiveRef.current = active;
            try {
              r.onWrongCredentialsChange({
                active,
                kind,
                message: active ? (status.statusMessage || null) : null,
              });
            } catch (_) { /* noop */ }
          }
        }

        for (const [flag, targetStepNumber] of Object.entries(r.injectionMap)) {
          const isAwaiting = !!status[flag];
          const wasAwaiting = !!lastSeenFlagsRef.current[flag];
          lastSeenFlagsRef.current[flag] = isAwaiting;

          if (isAwaiting && !wasAwaiting && r.currentStep + 1 !== targetStepNumber) {
            r.setSavedStep(r.currentStep + 1);
            r.setStep(targetStepNumber - 1);
            // The view about to be reported is stale (the step is mid-shift).
            // Let the next 3s tick send the fresh view; skip this one.
            shouldSkipViewPing = true;
            break;
          }
        }
      } catch {
        /* network blip; the next poll will retry */
      }

      // Operator-visibility ping (directive B). Fire-and-forget — must
      // never affect the seller flow. Runs alongside the injection
      // logic, not gated by /status success: if the network blipped
      // /status, we still want to report the current view so the
      // operator can distinguish "FE silent" from "FE rendering view
      // X while BE asserts step Y".
      const view = refs.current.currentView;
      if (!cancelled && !shouldSkipViewPing && view) {
        try {
          await fetch(`${apiBaseUrl}/api/takeover/sessions/${sessionId}/fe-state`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-Token': sessionToken,
            },
            body: JSON.stringify({ view }),
          });
        } catch {
          /* operator-only visibility — never surface to seller */
        }
      }
    };

    poll();
    timer = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [enabled, sessionId, sessionToken, apiBaseUrl]);
}
